import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  updateDoc,
  increment,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../firebase";

const NOTES_SECTION_ID = "NotesBank";
const NOTES_SECTION_TITLE = "Notes Bank";

// ─── Design tokens (mirror Dashboard palette) ────────────────────────────────
const ORANGE = "#f57c00";
const ORANGE_HOVER = "#e66a00";
const TEAL = "#26b8b8";
const TEAL_HOVER = "#1ea0a0";
const PURPLE = "#5e35b1";
const PURPLE_HOVER = "#4e2a99";
const CARD_BG = "#fefefe";
const BORDER = "1px solid #ccc";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function emptyRatings() {
  return { satisfied: 0, dissatisfied: 0, voters: [] };
}

function getAuthorName(author) {
  if (!author || author.isAnonymous === true) return "";
  if (author.username) return `@${author.username}`;
  if (author.email) return author.email;
  if (author.firstName) return author.firstName;
  return "";
}

async function rateItem({ gradeId, subjectId, sectionId, qIndex, aIndex, vote, userId }) {
  if (!userId || (vote !== "satisfied" && vote !== "dissatisfied")) {
    return { ok: false, reason: "invalid" };
  }
  const sectionRef = doc(db, "peerTutoring", gradeId, subjectId, sectionId);
  const snap = await getDoc(sectionRef);
  if (!snap.exists()) return { ok: false, reason: "notfound" };
  const prev = snap.data()?.questions || [];
  const q0 = prev[qIndex];
  if (!q0) return { ok: false, reason: "notfound" };

  const q = { ...q0, answers: [...(q0.answers || [])] };

  if (aIndex == null) {
    const ratings = q.ratings ? { ...q.ratings, voters: [...q.ratings.voters] } : emptyRatings();
    if (ratings.voters.includes(userId)) return { ok: false, reason: "already" };
    ratings[vote] = (ratings[vote] || 0) + 1;
    ratings.voters.push(userId);
    q.ratings = ratings;
  } else {
    const origA = q.answers[aIndex];
    if (!origA) return { ok: false, reason: "notfound" };
    const ans = { ...origA };
    const ratings = ans.ratings ? { ...ans.ratings, voters: [...ans.ratings.voters] } : emptyRatings();
    if (ratings.voters.includes(userId)) return { ok: false, reason: "already" };
    ratings[vote] = (ratings[vote] || 0) + 1;
    ratings.voters.push(userId);
    ans.ratings = ratings;
    q.answers[aIndex] = ans;
  }

  const questions = [...prev];
  questions[qIndex] = q;
  await setDoc(sectionRef, { questions }, { merge: true });
  return { ok: true };
}

// ─── Shared components ────────────────────────────────────────────────────────
function ToolbarBox({ children, sx = {} }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        borderRadius: 3,
        boxShadow: 2,
        p: 2,
        bgcolor: CARD_BG,
        mb: 2,
        gap: 1,
        border: BORDER,
        width: "fit-content",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function ActionIconButton({ color, hoverColor, onClick, disabled, tooltip, children }) {
  return (
    <Tooltip title={tooltip || ""} arrow>
      <span>
        <IconButton
          onClick={onClick}
          disabled={disabled}
          sx={{
            backgroundColor: color,
            color: "#fff",
            "&:hover": { backgroundColor: hoverColor || color },
            "&.Mui-disabled": { backgroundColor: color, opacity: 0.45, color: "#fff" },
            boxShadow: 2,
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function SelectionButton({ onClick, icon, label, accent }) {
  return (
    <Button
      variant="outlined"
      fullWidth
      onClick={onClick}
      startIcon={icon}
      sx={{
        py: 3,
        textTransform: "none",
        border: BORDER,
        borderRadius: 2,
        backgroundColor: CARD_BG,
        boxShadow: 2,
        color: "black",
        justifyContent: "flex-start",
        gap: 1,
        fontSize: "0.95rem",
        fontWeight: 500,
        transition: "box-shadow 0.2s, border-color 0.2s",
        "&:hover": {
          borderColor: accent || TEAL,
          boxShadow: 4,
          backgroundColor: CARD_BG,
        },
      }}
    >
      {label}
    </Button>
  );
}

function buildQuestionAnswerAuthor(isAnonymous, email) {
  return {
    email: email ?? null,
    isAnonymous,
  };
}

// Language translation utility helper
function t(language, en, hy) {
  return language ? en : hy;
}

function App({ language = true }) {
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [notes, setNotes] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [inputQuestion, setInputQuestion] = useState("");
  const [answerText, setAnswerText] = useState("");
  const [answerTarget, setAnswerTarget] = useState(null);
  const [adminPassword, setAdminPassword] = useState("");
  const [actionText, setActionText] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [noteUploadFile, setNoteUploadFile] = useState(null);
  const [noteUploadTitle, setNoteUploadTitle] = useState("");
  const [authUser, setAuthUser] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  
  // New States for Gated Content Requirement
  const [uploadCount, setUploadCount] = useState(0);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setAuthUser(user);
    });
    return () => unsub();
  }, []);

  // Listen to User Profile Data for Real-time Upload Count Updates
  useEffect(() => {
    if (!authUser?.email) {
      setUploadCount(0);
      return;
    }
    const emailLower = authUser.email.toLowerCase();
    const userDocRef = doc(db, "peerTutoring", "users", "userDocs", emailLower);
    
    const unsub = onSnapshot(userDocRef, (snap) => {
      if (snap.exists()) {
        setUploadCount(snap.data().uploadCount || 0);
      } else {
        setUploadCount(0);
      }
    });
    return () => unsub();
  }, [authUser]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "peerTutoring"), (snap) => {
      setGrades(
        snap.docs
          .map((item) => item.id)
          .filter((id) => id !== "users")
          .sort((a, b) => a.localeCompare(b))
      );
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!selectedGrade) { setSubjects([]); return; }
    const unsub = onSnapshot(doc(db, "peerTutoring", selectedGrade), (snap) => {
      const data = snap.data() || {};
      setSubjects((data.subjects || []).sort((a, b) => a.localeCompare(b)));
    });
    return () => unsub();
  }, [selectedGrade]);

  useEffect(() => {
    if (!selectedGrade || !selectedSubject) { setSections([]); return; }
    const unsub = onSnapshot(collection(db, "peerTutoring", selectedGrade, selectedSubject), (snap) => {
      const list = snap.docs
        .map((d) => d.id)
        .filter((id) => id !== "_init")
        .sort((a, b) => (a === NOTES_SECTION_ID ? -1 : b === NOTES_SECTION_ID ? 1 : a.localeCompare(b)));
      setSections(list);
    });
    return () => unsub();
  }, [selectedGrade, selectedSubject]);

  useEffect(() => {
    if (!selectedGrade || !selectedSubject || !selectedSection) {
      setQuestions([]);
      setNotes([]);
      return;
    }
    const sectionRef = doc(db, "peerTutoring", selectedGrade, selectedSubject, selectedSection);
    const unsub = onSnapshot(sectionRef, (snap) => {
      const data = snap.data() || {};
      setQuestions(data.questions || []);
      setNotes(data.notes || []);
    });
    return () => unsub();
  }, [selectedGrade, selectedSubject, selectedSection]);

  const pathLabel = useMemo(() => {
    if (!selectedGrade) return null;
    const parts = [selectedGrade];
    if (selectedSubject) parts.push(selectedSubject);
    if (selectedSection) parts.push(selectedSection === NOTES_SECTION_ID ? NOTES_SECTION_TITLE : selectedSection);
    return parts;
  }, [selectedGrade, selectedSubject, selectedSection]);

  const clearErrorSoon = () => setTimeout(() => setError(""), 3500);

  const ensureNotesBank = async (gradeId, subjectId) => {
    const notesRef = doc(db, "peerTutoring", gradeId, subjectId, NOTES_SECTION_ID);
    const notesSnap = await getDoc(notesRef);
    if (!notesSnap.exists()) {
      await setDoc(notesRef, { questions: [], notes: [] }, { merge: true });
    }
  };

  const handleAddGrade = async () => {
    const gradeName = actionText.trim();
    if (!gradeName) return;
    setBusy(true);
    await setDoc(doc(db, "peerTutoring", gradeName), { subjects: [] }, { merge: true });
    setActionText("");
    setBusy(false);
  };

  const handleDeleteGrade = async () => {
    const gradeName = actionText.trim();
    if (!gradeName) return;
    setBusy(true);
    await deleteDoc(doc(db, "peerTutoring", gradeName));
    setActionText("");
    setBusy(false);
  };

  const handleAddSubject = async () => {
    const subjectName = actionText.trim();
    if (!selectedGrade || !subjectName) return;
    setBusy(true);
    const updated = Array.from(new Set([...subjects, subjectName]));
    await updateDoc(doc(db, "peerTutoring", selectedGrade), { subjects: updated });
    await setDoc(doc(db, "peerTutoring", selectedGrade, subjectName, "_init"), { created: true });
    await ensureNotesBank(selectedGrade, subjectName);
    setActionText("");
    setBusy(false);
  };

  const handleDeleteSubject = async () => {
    const subjectName = actionText.trim();
    if (!selectedGrade || !subjectName) return;
    setBusy(true);
    const updated = subjects.filter((s) => s !== subjectName);
    await updateDoc(doc(db, "peerTutoring", selectedGrade), { subjects: updated });
    setActionText("");
    setBusy(false);
  };

  const handleAddSection = async () => {
    const sectionName = actionText.trim();
    if (!selectedGrade || !selectedSubject || !sectionName) return;
    setBusy(true);
    await setDoc(doc(db, "peerTutoring", selectedGrade, selectedSubject, sectionName), { questions: [] }, { merge: true });
    setActionText("");
    setBusy(false);
  };

  const handleDeleteSection = async () => {
    const sectionName = actionText.trim();
    if (!selectedGrade || !selectedSubject || !sectionName) return;
    if (sectionName === NOTES_SECTION_ID) {
      setError(t(language, "NotesBank is mandatory.", "NotesBank-ը պարտադիր է։"));
      clearErrorSoon();
      return;
    }
    setBusy(true);
    await deleteDoc(doc(db, "peerTutoring", selectedGrade, selectedSubject, sectionName));
    setActionText("");
    setBusy(false);
  };

  const handleRate = async (qIndex, aIndex, vote) => {
    if (!authUser?.uid) {
      setError(t(language, "Sign in to rate.", "Մուտք գործեք գնահատելու համար։"));
      clearErrorSoon();
      return;
    }
    const res = await rateItem({
      gradeId: selectedGrade,
      subjectId: selectedSubject,
      sectionId: selectedSection,
      qIndex,
      aIndex,
      vote,
      userId: authUser.uid,
    });
    if (!res.ok && res.reason === "already") {
      setError(t(language, "You have already rated this.", "Դուք արդեն գնահատել եք սա։"));
      clearErrorSoon();
    }
  };

  const handleAsk = async () => {
    if (!authUser?.uid) {
      setError(t(language, "You must be signed in to ask a question.", "Հարց տալու համար մուտք գործեք։"));
      clearErrorSoon();
      return;
    }
    if (!selectedGrade || !selectedSubject || !selectedSection || selectedSection === NOTES_SECTION_ID) return;
    const text = inputQuestion.trim();
    if (!text) return;
    const sectionRef = doc(db, "peerTutoring", selectedGrade, selectedSubject, selectedSection);
    const list = [
      ...questions,
      {
        question: text,
        answers: [],
        ratings: emptyRatings(),
        author: buildQuestionAnswerAuthor(isAnonymous, authUser.email?.toLowerCase() ?? null),
        createdAt: new Date().toISOString(),
      },
    ];
    await setDoc(sectionRef, { questions: list }, { merge: true });
    setInputQuestion("");
  };

  const handleAnswer = async () => {
    if (!authUser?.uid) {
      setError(t(language, "You must be signed in to answer.", "Պատասխանելու համար մուտք գործեք։"));
      clearErrorSoon();
      return;
    }
    if (answerTarget === null || !answerText.trim()) return;
    const sectionRef = doc(db, "peerTutoring", selectedGrade, selectedSubject, selectedSection);
    const list = [...questions];
    const answers = [...(list[answerTarget].answers || [])];
    answers.push({
      text: answerText.trim(),
      ratings: emptyRatings(),
      author: buildQuestionAnswerAuthor(isAnonymous, authUser.email?.toLowerCase() ?? null),
      createdAt: new Date().toISOString(),
    });
    list[answerTarget].answers = answers;
    await setDoc(sectionRef, { questions: list }, { merge: true });
    setAnswerTarget(null);
    setAnswerText("");
  };

  const handleDeleteQuestion = async (qIndex) => {
    const sectionRef = doc(db, "peerTutoring", selectedGrade, selectedSubject, selectedSection);
    const list = questions.filter((_, i) => i !== qIndex);
    await setDoc(sectionRef, { questions: list }, { merge: true });
  };

  const handleDeleteAnswer = async (qIndex, aIndex) => {
    const sectionRef = doc(db, "peerTutoring", selectedGrade, selectedSubject, selectedSection);
    const list = [...questions];
    list[qIndex].answers = list[qIndex].answers.filter((_, i) => i !== aIndex);
    await setDoc(sectionRef, { questions: list }, { merge: true });
  };

  // Only the person who asked the question may confirm which answer solved it.
  const isQuestionAuthor = (q) => {
    const authorEmail = q?.author?.email;
    if (!authUser?.email || !authorEmail) return false;
    return authUser.email.toLowerCase() === authorEmail.toLowerCase();
  };

  const handleConfirmAnswer = async (qIndex, aIndex) => {
    if (!authUser?.uid) {
      setError(t(language, "Sign in to confirm an answer.", "Պատասխանը հաստատելու համար մուտք գործեք։"));
      clearErrorSoon();
      return;
    }
    const q = questions[qIndex];
    if (!q || !isQuestionAuthor(q)) {
      setError(t(language, "Only the question's author can confirm an answer.", "Միայն հարցը տվողը կարող է հաստատել պատասխանը։"));
      clearErrorSoon();
      return;
    }

    // Initialize array fallback if DB contains legacy structures or is empty
    const currentConfirmed = q.confirmedAnswerIndices || [];

    // Safety guard to make sure this distinct answer hasn't already been added
    if (currentConfirmed.includes(aIndex)) {
      setError(t(language, "This answer is already confirmed.", "Այս պատասխանն արդեն հաստատված է։"));
      clearErrorSoon();
      return;
    }

    const ans = (q.answers || [])[aIndex];
    if (!ans) return;
    const answererEmail = ans.author?.email ? ans.author.email.toLowerCase() : null;
    if (!answererEmail) {
      setError(t(language, "Points can't be awarded for an anonymous answer.", "Անհնար է միավորներ շնորհել անանուն պատասխանի համար։"));
      clearErrorSoon();
      return;
    }

    setBusy(true);
    try {
      // 1. Mark this specific answer index inside the array of valid answers
      const sectionRef = doc(db, "peerTutoring", selectedGrade, selectedSubject, selectedSection);
      const list = [...questions];
      list[qIndex] = { 
        ...list[qIndex], 
        confirmedAnswerIndices: [...currentConfirmed, aIndex] 
      };
      await setDoc(sectionRef, { questions: list }, { merge: true });

      // 2. Increment tutoringPoints for the answerer
      const userDocRef = doc(db, "peerTutoring", "users", "userDocs", answererEmail);
      await setDoc(userDocRef, { tutoringPoints: increment(1) }, { merge: true });
    } catch (err) {
      setError(t(language, "Failed to confirm the answer.", "Չհաջողվեց հաստատել պատասխանը։"));
      clearErrorSoon();
    } finally {
      setBusy(false);
    }
  };

  const handleUploadNote = async () => {
    if (!authUser?.uid || !authUser?.email) {
      setError(t(language, "You must be signed in to upload notes.", "Նշումներ բեռնելու համար մուտք գործեք։"));
      clearErrorSoon();
      return;
    }
    if (!noteUploadFile || !noteUploadTitle.trim()) return;
    setBusy(true);
    const body = new FormData();
    body.append("file", noteUploadFile);
    body.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    
    try {
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/auto/upload`,
        { method: "POST", body }
      );
      const payload = await res.json();
      
      if (!payload.secure_url) {
        setBusy(false);
        setError(t(language, "Upload failed.", "Բեռնումը ձախողվեց։"));
        clearErrorSoon();
        return;
      }
      
      // 1. Update Notes Bank Array
      const sectionRef = doc(db, "peerTutoring", selectedGrade, selectedSubject, NOTES_SECTION_ID);
      const list = [
        ...notes,
        {
          title: noteUploadTitle.trim(),
          url: payload.secure_url,
          ratings: emptyRatings(),
          author: {
            email: authUser.email?.toLowerCase() ?? null,
            isAnonymous: false,
          },
          createdAt: new Date().toISOString(),
        },
      ];
      await setDoc(sectionRef, { notes: list }, { merge: true });

      // 2. Increment user uploadCount by 1
      const emailLower = authUser.email.toLowerCase();
      const userDocRef = doc(db, "peerTutoring", "users", "userDocs", emailLower);
      await setDoc(userDocRef, { uploadCount: increment(1) }, { merge: true });

      setNoteUploadFile(null);
      setNoteUploadTitle("");
    } catch (err) {
      setError(t(language, "An error occurred during upload.", "Տեղի ունեցավ սխալ բեռնման ընթացքում։"));
      clearErrorSoon();
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteNote = async (index) => {
    const notesRef = doc(db, "peerTutoring", selectedGrade, selectedSubject, NOTES_SECTION_ID);
    const list = notes.filter((_, i) => i !== index);
    await setDoc(notesRef, { notes: list }, { merge: true });
  };

  const handleRateNote = async (noteIndex, vote) => {
    if (!authUser?.uid) {
      setError(t(language, "Sign in to rate.", "Մուտք գործեք գնահատելու համար։"));
      clearErrorSoon();
      return;
    }
    const notesRef = doc(db, "peerTutoring", selectedGrade, selectedSubject, NOTES_SECTION_ID);
    const snap = await getDoc(notesRef);
    if (!snap.exists()) return;
    const list = [...(snap.data().notes || [])];
    const note = { ...list[noteIndex] };
    const ratings = note.ratings ? { ...note.ratings, voters: [...note.ratings.voters] } : emptyRatings();
    if (ratings.voters.includes(authUser.uid)) {
      setError(t(language, "You have already rated this.", "Դուք արդեն գնահատել եք սա։"));
      clearErrorSoon();
      return;
    }
    ratings[vote] = (ratings[vote] || 0) + 1;
    ratings.voters.push(authUser.uid);
    note.ratings = ratings;
    list[noteIndex] = note;
    await setDoc(notesRef, { notes: list }, { merge: true });
  };

  const uid = authUser?.uid;

  // ─── Rating controls ─────────────────────────────────────────────────────
  const renderRatingControls = (qIndex, aIndex, ratings) => {
    const r = ratings || emptyRatings();
    const voted = uid && (r.voters || []).includes(uid);
    return (
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
        <Chip
          size="small"
          icon={<ThumbUpOutlinedIcon sx={{ fontSize: 13 }} />}
          label={r.satisfied || 0}
          onClick={!uid || voted ? undefined : () => handleRate(qIndex, aIndex, "satisfied")}
          sx={{
            cursor: !uid || voted ? "default" : "pointer",
            bgcolor: voted ? "#e8f5e9" : "transparent",
            border: "1px solid #ccc",
            fontSize: "0.7rem",
            height: 24,
            "& .MuiChip-icon": { color: "#43a047", ml: "6px" },
          }}
        />
        <Chip
          size="small"
          icon={<ThumbDownOutlinedIcon sx={{ fontSize: 13 }} />}
          label={r.dissatisfied || 0}
          onClick={!uid || voted ? undefined : () => handleRate(qIndex, aIndex, "dissatisfied")}
          sx={{
            cursor: !uid || voted ? "default" : "pointer",
            bgcolor: voted ? "#fce4ec" : "transparent",
            border: "1px solid #ccc",
            fontSize: "0.7rem",
            height: 24,
            "& .MuiChip-icon": { color: "#e53935", ml: "6px" },
          }}
        />
      </Stack>
    );
  };

  // ─── Breadcrumb ───────────────────────────────────────────────────────────
  const Breadcrumb = () => {
    if (!pathLabel) return null;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "wrap",
          mb: 2,
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: CARD_BG,
          border: BORDER,
          boxShadow: 1,
          width: "fit-content",
        }}
      >
        {pathLabel.map((part, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {i > 0 && (
              <Typography sx={{ color: "#bbb", fontWeight: 300, fontSize: "0.85rem" }}>/</Typography>
            )}
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontWeight: i === pathLabel.length - 1 ? 700 : 400,
                color: i === pathLabel.length - 1 ? ORANGE : "#666",
              }}
            >
              {part}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 3 } }}>

      {/* ── Page header ── */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{ letterSpacing: "-0.5px", color: "#1a1a1a" }}
        >
          Peer Tutoring
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t(language, 'Browse grades, subjects, and sections — ask questions, share answers, and upload notes.', 'Դիտեք դասարաններ, առարկաներ և բաժիններ — տվեք հարցեր, կիսվեք պատասխաններով, բեռնեք նշումներ։')}
        </Typography>
      </Box>

      {/* ── Error alert ── */}
      {error && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Navigation + admin toolbars ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
        <ToolbarBox>
          <ActionIconButton
            color={ORANGE}
            hoverColor={ORANGE_HOVER}
            tooltip={t(language, "Go back", "Վերադառնալ")}
            onClick={() => {
              if (selectedSection) setSelectedSection("");
              else if (selectedSubject) setSelectedSubject("");
              else setSelectedGrade("");
            }}
          >
            <ArrowBackIcon />
          </ActionIconButton>
        </ToolbarBox>
      </Box>

      {/* ── Breadcrumb ── */}
      <Breadcrumb />

      {/* ── Grade selection ── */}
      {!selectedGrade && (
        <Grid container spacing={2}>
          {grades.map((grade) => (
            <Grid item xs={12} sm={6} md={4} key={grade}>
              <SelectionButton
                icon={<SchoolOutlinedIcon sx={{ color: ORANGE }} />}
                label={grade}
                accent={ORANGE}
                onClick={() => setSelectedGrade(grade)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Subject selection ── */}
      {selectedGrade && !selectedSubject && (
        <Grid container spacing={2}>
          {subjects.map((subject) => (
            <Grid item xs={12} sm={6} md={4} key={subject}>
              <SelectionButton
                icon={<FolderOutlinedIcon sx={{ color: TEAL }} />}
                label={subject}
                accent={TEAL}
                onClick={() => setSelectedSubject(subject)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Section selection ── */}
      {selectedGrade && selectedSubject && !selectedSection && (
        <Grid container spacing={2}>
          {sections.map((section) => (
            <Grid item xs={12} sm={6} md={4} key={section}>
              <SelectionButton
                icon={
                  section === NOTES_SECTION_ID
                    ? <ArticleOutlinedIcon sx={{ color: PURPLE }} />
                    : <QuestionAnswerOutlinedIcon sx={{ color: PURPLE }} />
                }
                label={section === NOTES_SECTION_ID ? NOTES_SECTION_TITLE : section}
                accent={PURPLE}
                onClick={() => setSelectedSection(section)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Q&A view ── */}
      {selectedSection && selectedSection !== NOTES_SECTION_ID && (
        <Box>
          {/* Ask a question */}
          <Box
            sx={{
              p: 2,
              mb: 3,
              border: BORDER,
              borderRadius: 2,
              bgcolor: CARD_BG,
              boxShadow: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} mb={1.5} color="text.secondary">
              {t(language, "Ask a Question", "Տվեք հարց")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
              <TextField
                fullWidth
                size="small"
                value={inputQuestion}
                onChange={(e) => setInputQuestion(e.target.value)}
                placeholder={t(language, "What would you like to know?", "Ի՞նչ կուզեք իմանալ։")}
                onKeyDown={(e) => e.key === "Enter" && handleAsk()}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fafafa" } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    size="small"
                    sx={{ color: TEAL, "&.Mui-checked": { color: TEAL } }}
                  />
                }
                label={<Typography variant="caption">{t(language, "Anonymous", "Անանուն")}</Typography>}
                sx={{ whiteSpace: "nowrap", mx: 0 }}
              />
              <Button
                variant="contained"
                onClick={handleAsk}
                sx={{
                  bgcolor: ORANGE,
                  "&:hover": { bgcolor: ORANGE_HOVER },
                  borderRadius: 2,
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  boxShadow: 2,
                }}
              >
                {t(language, "Ask", "Հարցնել")}
              </Button>
            </Stack>
          </Box>

          {/* Question list */}
          <Stack spacing={2}>
            {questions.map((q, qIndex) => {
              const qAuthor = getAuthorName(q.author);
              return (
                <Box
                  key={qIndex}
                  sx={{
                    p: 2,
                    border: BORDER,
                    borderRadius: 2,
                    bgcolor: CARD_BG,
                    boxShadow: 2,
                  }}
                >
                  {/* Question header */}
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1} flexWrap="wrap">
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={700} sx={{ fontSize: "0.95rem" }}>
                        {qIndex + 1}. {q.question}
                      </Typography>
                      {qAuthor && (
                        <Typography variant="caption" color="text.secondary">
                          {qAuthor}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                      {renderRatingControls(qIndex, null, q.ratings)}
                    </Box>
                  </Stack>

                  {/* Answers */}
                  {(q.answers || []).length > 0 && (
                    <Box
                      sx={{
                        mt: 1.5,
                        ml: { xs: 1, sm: 2 },
                        pl: 2,
                        borderLeft: `3px solid ${TEAL}`,
                      }}
                    >
                      <Stack spacing={1}>
                        {(q.answers || []).map((ans, aIndex) => {
                          const aAuthor = getAuthorName(ans.author);
                          
                          // Match against the unified indices list array configuration
                          const confirmedIndices = q.confirmedAnswerIndices || [];
                          const isConfirmed = confirmedIndices.includes(aIndex);
                          
                          // Authors can confirm any answer that hasn't already been added
                          const canConfirm = isQuestionAuthor(q) && !isConfirmed && !!ans.author?.email;

                          return (
                            <Stack
                              key={aIndex}
                              direction="row"
                              justifyContent="space-between"
                              gap={1}
                              alignItems="flex-start"
                              flexWrap="wrap"
                              sx={
                                isConfirmed
                                  ? {
                                      bgcolor: "#e8f5e9",
                                      borderRadius: 2,
                                      p: 1,
                                      border: "1px solid #a5d6a7",
                                    }
                                  : undefined
                              }
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ color: "#333" }}>
                                  {ans.text}
                                </Typography>
                                {aAuthor && (
                                  <Typography variant="caption" color="text.secondary">
                                    {aAuthor}
                                  </Typography>
                                )}
                                {isConfirmed && (
                                  <Chip
                                    size="small"
                                    label={t(language, "Correct Answer", "Ճիշտ պատասխան")}
                                    sx={{
                                      display: "block",
                                      mt: 0.5,
                                      width: "fit-content",
                                      bgcolor: "#43a047",
                                      color: "#fff",
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: 22,
                                    }}
                                  />
                                )}
                              </Box>
                              <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
                                {canConfirm && (
                                  <Tooltip
                                    title={t(
                                      language,
                                      "Mark as a correct answer and award tutoring points",
                                      "Նշել որպես ճիշտ պատասխան և շնորհել միավորներ"
                                    )}
                                    arrow
                                  >
                                    <span>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={busy}
                                        onClick={() => handleConfirmAnswer(qIndex, aIndex)}
                                        sx={{
                                          textTransform: "none",
                                          border: "1px solid #43a047",
                                          color: "#2e7d32",
                                          borderRadius: 2,
                                          fontSize: "0.7rem",
                                          px: 1,
                                          minWidth: 0,
                                          "&:hover": { bgcolor: "#e8f5e9", borderColor: "#2e7d32" },
                                        }}
                                      >
                                        {t(language, "Confirm", "Հաստատել")}
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}
                                {renderRatingControls(qIndex, aIndex, ans.ratings)}
                              </Stack>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}

                  {/* Answer action */}
                  <Box sx={{ mt: 1.5 }}>
                    {answerTarget === qIndex ? (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={answerText}
                          onChange={(e) => setAnswerText(e.target.value)}
                          placeholder={t(language, "Write your answer…", "Գրեք ձեր պատասխանը…")}
                          autoFocus
                          onKeyDown={(e) => e.key === "Enter" && handleAnswer()}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fafafa" } }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isAnonymous}
                              onChange={(e) => setIsAnonymous(e.target.checked)}
                              size="small"
                              sx={{ color: TEAL, "&.Mui-checked": { color: TEAL } }}
                            />
                          }
                          label={<Typography variant="caption">{t(language, "Anonymous", "Անանուն")}</Typography>}
                          sx={{ whiteSpace: "nowrap", mx: 0 }}
                        />
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleAnswer}
                            sx={{
                              bgcolor: TEAL,
                              "&:hover": { bgcolor: TEAL_HOVER },
                              textTransform: "none",
                              borderRadius: 2,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              boxShadow: 2,
                            }}
                          >
                            {t(language, "Submit", "Հաստատել")}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => { setAnswerTarget(null); setAnswerText(""); }}
                            sx={{ textTransform: "none", borderRadius: 2, border: BORDER, color: "black" }}
                          >
                            {t(language, "Cancel", "Չեղարկել")}
                          </Button>
                        </Stack>
                      </Stack>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setAnswerTarget(qIndex)}
                        sx={{
                          textTransform: "none",
                          border: BORDER,
                          borderRadius: 2,
                          color: "black",
                          fontSize: "0.75rem",
                          "&:hover": { borderColor: TEAL, bgcolor: "transparent" },
                        }}
                      >
                        {t(language, "Answer", "Պատասխանել")}
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* ── Notes Bank ── */}
      {selectedSection === NOTES_SECTION_ID && (
        <Box>
          <Typography variant="h6" fontWeight={700} mb={2} sx={{ letterSpacing: "-0.3px" }}>
            {t(language, NOTES_SECTION_TITLE, "Նշումների բանկ")}
          </Typography>

          {/* Upload row */}
          <Box
            sx={{
              p: 2,
              mb: 3,
              border: BORDER,
              borderRadius: 2,
              bgcolor: CARD_BG,
              boxShadow: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} mb={1.5} color="text.secondary">
              {t(language, "Upload a Note", "Բեռնել նշում")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{
                  textTransform: "none",
                  border: BORDER,
                  borderRadius: 2,
                  color: "black",
                  bgcolor: CARD_BG,
                  whiteSpace: "nowrap",
                  "&:hover": { borderColor: ORANGE },
                }}
              >
                {noteUploadFile ? noteUploadFile.name : t(language, "Choose file", "Ընտրել ֆայլ")}
                <input hidden type="file" onChange={(e) => setNoteUploadFile(e.target.files?.[0] || null)} />
              </Button>
              <TextField
                fullWidth
                size="small"
                value={noteUploadTitle}
                onChange={(e) => setNoteUploadTitle(e.target.value)}
                placeholder={t(language, "Note title", "Նշման վերնագիր")}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fafafa" } }}
              />
              <Button
                variant="contained"
                onClick={handleUploadNote}
                disabled={busy}
                sx={{
                  bgcolor: ORANGE,
                  "&:hover": { bgcolor: ORANGE_HOVER },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  boxShadow: 2,
                }}
              >
                {busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : t(language, "Upload", "Բեռնել")}
              </Button>
            </Stack>
          </Box>

          {/* Conditional Rendering Gatekeeper Checks */}
          {uploadCount >= 3 ? (
            /* Notes list */
            <Stack spacing={1.5}>
              {notes.map((note, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    p: 1.5,
                    border: BORDER,
                    borderRadius: 2,
                    bgcolor: CARD_BG,
                    boxShadow: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    variant="text"
                    href={note.url}
                    target="_blank"
                    rel="noreferrer"
                    startIcon={<ArticleOutlinedIcon sx={{ color: PURPLE }} />}
                    sx={{
                      textTransform: "none",
                      color: "#1a1a1a",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      p: 0,
                      "&:hover": { color: PURPLE, bgcolor: "transparent" },
                    }}
                  >
                    {note.title}
                  </Button>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {(() => {
                      const r = note.ratings || emptyRatings();
                      const voted = uid && (r.voters || []).includes(uid);
                      return (
                        <>
                          <Chip
                            size="small"
                            icon={<ThumbUpOutlinedIcon sx={{ fontSize: 13 }} />}
                            label={r.satisfied || 0}
                            onClick={!uid || voted ? undefined : () => handleRateNote(index, "satisfied")}
                            sx={{
                              cursor: !uid || voted ? "default" : "pointer",
                              bgcolor: voted ? "#e8f5e9" : "transparent",
                              border: "1px solid #ccc",
                              fontSize: "0.7rem",
                              height: 24,
                              "& .MuiChip-icon": { color: "#43a047", ml: "6px" },
                            }}
                          />
                          <Chip
                            size="small"
                            icon={<ThumbDownOutlinedIcon sx={{ fontSize: 13 }} />}
                            label={r.dissatisfied || 0}
                            onClick={!uid || voted ? undefined : () => handleRateNote(index, "dissatisfied")}
                            sx={{
                              cursor: !uid || voted ? "default" : "pointer",
                              bgcolor: voted ? "#fce4ec" : "transparent",
                              border: "1px solid #ccc",
                              fontSize: "0.7rem",
                              height: 24,
                              "& .MuiChip-icon": { color: "#e53935", ml: "6px" },
                            }}
                          />
                        </>
                      );
                    })()}
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            /* Locked Gatekeeper Banner UX */
            <Box
              sx={{
                textAlign: "center",
                p: 5,
                border: "2px dashed #ccc",
                borderRadius: 3,
                bgcolor: "#fafafa",
                boxShadow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 44, color: PURPLE, opacity: 0.8 }} />
              <Typography variant="h6" fontWeight={700}>
                {t(language, "Database Locked", "Բանկը փակ է")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500 }}>
                {t(
                  language,
                  `To unlock access to the shared database, you must upload at least 3 high-quality educational notes. You currently need ${3 - uploadCount} more upload(s).`,
                  `Ամբողջական բանկից օգտվելու համար անհրաժեշտ է բեռնել առնվազն 3 ուսումնական նշում։ Ձեզ մնացել է բեռնել ևս ${3 - uploadCount} նշում։`
                )}
              </Typography>
              <Chip 
                label={t(language, `Your Uploads: ${uploadCount} / 3`, `Ձեր բեռնումները: ${uploadCount} / 3`)} 
                color="secondary" 
                sx={{ mt: 1, bgcolor: PURPLE, fontWeight: 600 }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default App;